import React, { useContext, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Container } from "react-bootstrap";

import GlobalContext from "../../context/GlobalContext";

const ROLES = [
  "DevOps Junior",
  "SRE Engineer",
  "Platform Engineer",
  "Infrastructure Lead",
];

const scan = keyframes`
  0% { transform: translateY(-120%); }
  100% { transform: translateY(220%); }
`;

const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0.2; }
`;

const SectionStyled = styled.section`
  padding: 128px 0;
  background: var(--nd-black);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, var(--nd-border) 0.5px, transparent 0.5px);
    background-size: 12px 12px;
    opacity: 0.25;
    pointer-events: none;
  }
`;

const ScanLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 30%;
  pointer-events: none;
  background: linear-gradient(to bottom, transparent, rgba(255,255,255,0.04), transparent);
  animation: ${scan} 6s linear infinite;
`;

const Inner = styled.div`
  position: relative;
  text-align: center;
  max-width: 720px;
  margin: 0 auto;
`;

const Label = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin-bottom: 24px;
`;

const RecruitDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--nd-success);
  animation: ${blink} 1.4s steps(1) infinite;
`;

const Title = styled.h2`
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 56px;
  font-weight: 700;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: 0 0 16px;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const RoleRotator = styled.div`
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  font-family: 'Space Mono', monospace;
  font-size: 16px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin-bottom: 32px;
`;

const Bracket = styled.span`
  color: var(--nd-text-disabled);
`;

const RoleText = styled.span`
  display: inline-block;
  min-width: 260px;
  text-align: center;
  transition: opacity 250ms ease, transform 250ms ease;
  opacity: ${({ fading }) => (fading ? 0 : 1)};
  transform: ${({ fading }) => (fading ? "translateY(6px)" : "translateY(0)")};
`;

const Desc = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-secondary);
  line-height: 1.6;
  margin: 0 auto 48px;
  max-width: 520px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const BracketButton = styled.button`
  position: relative;
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 14px 32px;
  border: 1px solid var(--nd-border-visible);
  background: transparent;
  color: var(--nd-text-primary);
  cursor: pointer;
  outline: none;
  border-radius: 0;
  text-decoration: none !important;
  transition: color 200ms ease, border-color 200ms ease;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 8px;
    height: 8px;
    transition: width 200ms ease, height 200ms ease, border-color 200ms ease;
  }
  &::before {
    top: -1px;
    left: -1px;
    border-top: 1px solid var(--nd-text-display);
    border-left: 1px solid var(--nd-text-display);
  }
  &::after {
    bottom: -1px;
    right: -1px;
    border-bottom: 1px solid var(--nd-text-display);
    border-right: 1px solid var(--nd-text-display);
  }

  &:hover {
    color: var(--nd-text-display);
    border-color: var(--nd-text-display);
  }
  &:hover::before,
  &:hover::after {
    width: 18px;
    height: 18px;
  }
`;

const PrimaryBracketButton = styled(BracketButton)`
  background: var(--nd-text-display);
  color: var(--nd-black);
  border-color: var(--nd-text-display);

  &::before {
    border-top-color: var(--nd-black);
    border-left-color: var(--nd-black);
  }
  &::after {
    border-bottom-color: var(--nd-black);
    border-right-color: var(--nd-black);
  }

  &:hover {
    color: var(--nd-black);
    border-color: var(--nd-text-display);
    opacity: 0.88;
  }
  &:hover::before {
    border-top-color: var(--nd-black);
    border-left-color: var(--nd-black);
  }
  &:hover::after {
    border-bottom-color: var(--nd-black);
    border-right-color: var(--nd-black);
  }
`;

const TeamCTA = () => {
  const gContext = useContext(GlobalContext);
  const [idx, setIdx] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const t = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setIdx((i) => (i + 1) % ROLES.length);
        setFading(false);
      }, 250);
    }, 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <SectionStyled>
      <ScanLine />
      <Container>
        <Inner>
          <Label>
            <RecruitDot />
            Now hiring
          </Label>
          <Title>Want to join?</Title>
          <RoleRotator>
            <Bracket>[</Bracket>
            <RoleText fading={fading}>{ROLES[idx]}</RoleText>
            <Bracket>]</Bracket>
          </RoleRotator>
          <Desc>
            We're a small team and we like it that way. If you're good at DevOps
            and bad at corporate politics, we should talk.
          </Desc>
          <ButtonRow>
            <PrimaryBracketButton
              type="button"
              aria-label="Book a call"
              onClick={(e) => {
                e.preventDefault();
                gContext.toggleContact();
              }}
            >
              Book a Call
            </PrimaryBracketButton>
            <BracketButton
              as="a"
              href="mailto:cv@dioscuro.com?subject=Resume"
              aria-label="Send your resume to cv@dioscuro.com"
            >
              Send your Resume
            </BracketButton>
          </ButtonRow>
        </Inner>
      </Container>
    </SectionStyled>
  );
};

export default TeamCTA;
